import { View, Text, Button, Flex, Spinner, IconVideoCameraOffLine } from '@smarterservices/smarter-ui';
import IntroContent from './IntroContent';

/**
 * Renders a full-size absolutely positioned overlay with centered content.
 * Used internally to display status messages on top of the (hidden) iframe.
 *
 * @param {JSX.Element} content - The content to render inside the overlay
 * @returns {JSX.Element}
 */
const CenteredOverlay = ({ children }) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fff',
      zIndex: 1,
    }}
  >
    {children}
  </div>
);

/**
 * StatusOverlays — Renders the appropriate overlay for the current remote control state.
 *
 * Overlay states (in priority order):
 * 1. Not connected to live room → "Must be connected" message
 * 2. No uniqueId → Intro text with instructions and start button
 * 3. uniqueId but no watchUrl → "Connecting..." spinner
 * 4. watchUrl + waiting/null status → "Waiting for browser..." spinner
 * 5. watchUrl + pendingRequest → "Waiting for permission..." spinner
 * 6. watchUrl + terminated → "Session ended" with reconnect button
 * 7. watchUrl + active/viewerReady → No overlay (iframe is visible)
 *
 * @param {Object} props
 * @param {boolean} props.isRoomConnected - Whether the LiveKit room is connected
 * @param {string|null} props.uniqueId - The remote control session unique ID
 * @param {string|null} props.watchUrl - The Upscope co-browsing watch URL
 * @param {string|null} props.remoteControlStatus - Current status from Upscope iframe
 * @param {Function} props.onStart - Callback to start a new remote control session
 * @param {boolean} props.isStarting - Whether a start attempt is in progress
 * @param {string|null} props.error - Error message from a failed start attempt
 * @param {Function} props.onReconnect - Callback to re-inject the remote control script
 * @param {boolean} props.isReconnecting - Whether a reconnect attempt is in progress
 * @returns {JSX.Element|null}
 */
function StatusOverlays({
  isRoomConnected,
  uniqueId,
  watchUrl,
  remoteControlStatus,
  onStart,
  isStarting,
  error,
  onReconnect,
  isReconnecting,
}) {
  // 1. Not connected to a live room
  if (!isRoomConnected && !uniqueId) {
    return (
      <CenteredOverlay>
        <View as="div" margin="none none small none" padding="none large">
          <View as="div" margin="none none large none" textAlign="center">
            <IconVideoCameraOffLine size="medium" />
          </View>
          <Text>Must be connected to live session to start remote control</Text>
        </View>
      </CenteredOverlay>
    );
  }

  // 2. Connected but no session started
  if (isRoomConnected && !uniqueId) {
    return (
      <CenteredOverlay>
        <IntroContent onStart={onStart} isStarting={isStarting} error={error} />
      </CenteredOverlay>
    );
  }

  // 3. Session started, waiting for watchUrl
  if (uniqueId && !watchUrl) {
    return (
      <CenteredOverlay>
        <Spinner size="medium" renderTitle="Connecting to remote session" />
        <View as="div" margin="small none none none">
          <Text>Connecting to remote control session, this can take up to a minute...</Text>
        </View>
      </CenteredOverlay>
    );
  }

  // 4. watchUrl available, waiting for browser
  if (watchUrl && (remoteControlStatus === 'waiting' || remoteControlStatus === null)) {
    return (
      <CenteredOverlay>
        <Spinner size="medium" renderTitle="Waiting for browser" />
        <View as="div" margin="small none none none">
          <Text size="small">Waiting for the browser to respond, this can take up to a minute...</Text>
        </View>
      </CenteredOverlay>
    );
  }

  // 5. watchUrl available, waiting for permission
  if (watchUrl && remoteControlStatus === 'pendingRequest') {
    return (
      <CenteredOverlay>
        <Spinner size="medium" renderTitle="Waiting for permission" />
        <View as="div" margin="small none none none">
          <Text size="small">Waiting for user to allow permission...</Text>
        </View>
      </CenteredOverlay>
    );
  }

  // 6. Session terminated
  if (watchUrl && remoteControlStatus === 'terminated') {
    return (
      <CenteredOverlay>
        <View as="div" margin="none none small none">
          <Text size="medium" weight="bold">Remote control session ended.</Text>
        </View>
        <Button
          color="primary"
          onClick={onReconnect}
          interaction={isReconnecting ? 'disabled' : 'enabled'}
        >
          {isReconnecting ? (
            <Flex alignItems="center" gap="x-small">
              <Spinner size="x-small" renderTitle="Reconnecting" />
              <Text size="small">Reconnecting...</Text>
            </Flex>
          ) : (
            'Connect Again'
          )}
        </Button>
      </CenteredOverlay>
    );
  }

  // 7. Active / viewerReady — no overlay needed
  return null;
}

export default StatusOverlays;
